// Backlog #34 (handover templates) and #36 (customisable "Report an issue" form): sectioned, versioned forms scoped to a
// building, team or position. Built on the form-channel field engine (./forms) so both share one validator and one builder.
import { FORM_FIELD_TYPES, validateFormFields, validateFormValues, type FormField, type FormFieldType, type FormValues } from './forms';

export const SCOPED_FORM_KINDS = ['handover', 'incident'] as const;
export type ScopedFormKind = (typeof SCOPED_FORM_KINDS)[number];
export const FORM_SCOPE_TYPES = ['workspace', 'location', 'team', 'position'] as const;
export type FormScopeType = (typeof FORM_SCOPE_TYPES)[number];

/** Which scopes each kind supports, most specific first (resolution order). */
export const KIND_SCOPES: Record<ScopedFormKind, FormScopeType[]> = {
  handover: ['position', 'team', 'location', 'workspace'],
  incident: ['team', 'location', 'workspace'],
};
/** Field types offered in the sectioned builder (no people/building pickers: the context already says where). */
export const SCOPED_FIELD_TYPES: FormFieldType[] = ['short_text', 'long_text', 'checkbox', 'number', 'single_select', 'multi_select', 'date', 'time', 'file'];
export const SCOPED_FORM_LIMITS = { sections: 12, fields: 60, title: 120, versionTitle: 120, description: 300 } as const;

export interface FormSection { id: string; title: string; description?: string | null; fields: FormField[] }

export interface ScopedForm {
  id: string; kind: ScopedFormKind; name: string; emoji?: string | null; description?: string | null;
  scope_type: FormScopeType; scope_id: string | null; scope_name: string | null;
  version: number; version_title: string; sections: FormSection[];
  updated_at: string; updated_by_name: string | null; archived_at: string | null;
}
export interface ScopedFormVersion {
  version: number; title: string; sections: FormSection[]; created_by: string; created_by_name: string; created_at: string; restored_from: number | null;
}
/** Where a handover / report is being made; used to pick the template. */
export interface FormContext {
  location_id: string | null; team_id?: string | null; position_id?: string | null;
  /** Issue reports: the reporter's teams (a team-scoped form applies to its members). */
  team_ids?: string[];
}
export interface ResolvedForms { applied: ScopedForm | null; options: ScopedForm[] }

export function sectionFields(sections: FormSection[]): FormField[] {
  return (sections ?? []).flatMap(s => s.fields ?? []);
}

const SECTION_ID_RE = /^[a-z][a-z0-9_]{0,39}$/;

/** Builder- and server-side validation of a sectioned form. Field ids are unique across the whole form. */
export function validateSections(sections: FormSection[]): string[] {
  if (!Array.isArray(sections) || sections.length === 0) return ['Add at least one section'];
  const errs: string[] = [];
  if (sections.length > SCOPED_FORM_LIMITS.sections) errs.push(`At most ${SCOPED_FORM_LIMITS.sections} sections`);
  const ids = new Set<string>();
  sections.forEach((s, i) => {
    const n = `Section ${i + 1}${s?.title ? ` (“${s.title}”)` : ''}`;
    if (!s || typeof s !== 'object') { errs.push(`${n}: invalid`); return; }
    if (!SECTION_ID_RE.test(s.id ?? '')) errs.push(`${n}: invalid id`);
    else if (ids.has(s.id)) errs.push(`${n}: duplicate id`);
    ids.add(s.id);
    if (typeof s.title !== 'string' || !s.title.trim() || s.title.length > SCOPED_FORM_LIMITS.title) errs.push(`${n}: title must be 1-${SCOPED_FORM_LIMITS.title} characters`);
    if (s.description && s.description.length > SCOPED_FORM_LIMITS.description) errs.push(`${n}: description too long`);
    // t725u: a section can be just a heading (its fields come later or it introduces the next part of the form).
    if (!Array.isArray(s.fields)) errs.push(`${n}: invalid`);
    for (const f of s.fields ?? []) if (f && !SCOPED_FIELD_TYPES.includes(f.type) && (FORM_FIELD_TYPES as readonly string[]).includes(f.type)) errs.push(`${n}: “${f.label}” uses a field type not available here`);
  });
  const all = sectionFields(sections);
  if (!all.length) errs.push('Add at least one field');
  if (all.length > SCOPED_FORM_LIMITS.fields) errs.push(`At most ${SCOPED_FORM_LIMITS.fields} fields`);
  if (all.length) errs.push(...validateFormFields(all).filter(e => !e.startsWith('At most')));
  return errs;
}

/** Validate answers against a version; unknown keys are dropped. */
export function validateSectionAnswers(sections: FormSection[], raw: Record<string, unknown>): { values: FormValues; errors: Record<string, string> } {
  return validateFormValues(sectionFields(sections), raw ?? {});
}

/** Does a form's scope apply in this context? */
export function scopeMatches(f: Pick<ScopedForm, 'scope_type' | 'scope_id'>, c: FormContext): boolean {
  switch (f.scope_type) {
    case 'workspace': return true;
    case 'location': return !!c.location_id && f.scope_id === c.location_id;
    case 'team': return (!!c.team_id && f.scope_id === c.team_id) || (!!f.scope_id && !!c.team_ids?.includes(f.scope_id));
    case 'position': return !!c.position_id && f.scope_id === c.position_id;
  }
}

/** Allowed forms for a context, most specific first; `applied` is the first (position > team > building > workspace). */
export function resolveScopedForms(kind: ScopedFormKind, forms: ScopedForm[], c: FormContext): ResolvedForms {
  const order = KIND_SCOPES[kind];
  const options = forms
    .filter(f => f.kind === kind && !f.archived_at && order.includes(f.scope_type) && scopeMatches(f, c))
    .sort((a, b) => order.indexOf(a.scope_type) - order.indexOf(b.scope_type) || a.name.localeCompare(b.name));
  return { applied: options[0] ?? null, options };
}

export const FORM_SCOPE_LABELS: Record<FormScopeType, string> = { workspace: 'Whole workspace', location: 'Building', team: 'Team', position: 'Position' };
